/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ContactDeliveryMethod,
  ContactDetailsCreditorCommonDto,
  ContactDetailsCreditorDeliveryInfoDto,
  ContactDetailsCreditorDto,
  ContactDetailsCreditorSubContractorDto, CreditorType,
  EmailMatch,
} from '@gh/builder-shared-data';
import { ContactDetails } from './contact-details.model';

export type ContactDetailsCreditor = ContactDetailsCreditorDto;

export type ContactDetailsCreditorCommon = ContactDetailsCreditorCommonDto;
export type ContactDetailsCreditorDeliveryInfo = ContactDetailsCreditorDeliveryInfoDto;

export type CreditorDestinationType = 'orderBy' | 'billingBy' | 'scheduleBy';

export type ContactDetailsCreditorSubContractor = ContactDetailsCreditorSubContractorDto ;

export function getCreditorOriginalValue(current: ContactDetails,
                                         original: ContactDetails): ContactDetailsCreditor {
  const { creditorSupplier } = original;

  return {
    ...creditorSupplier!, // tslint:disable-line:no-non-null-assertion
    common: getCreditorCommonOriginalValue(current, original),
  };
}

export function contactDetailsCreditorFromDto(dto: ContactDetailsCreditorDto,
                                              root: ContactDetails): ContactDetailsCreditor {
  return {
    ...dto,
    common: contactDetailsCreditorCommonFromDto(dto.common, root),
  };
}

export function contactDetailsCreditorToDto(model: ContactDetailsCreditor): ContactDetailsCreditorDto {
  const hasSubContractor = model.common.type === CreditorType.SubContractor;

  return {
    ...model,
    subContractor: hasSubContractor ? model.subContractor : <any>{},
  };
}

/**
 * This method merges original information about creditor/supplier with current contact information.
 * This method is called when corresponding contact type is assigned.
 *
 * @param current
 * @param original
 */
export function getCreditorCommonOriginalValue(current: ContactDetails,
                                               original: ContactDetails): ContactDetailsCreditorCommon {
  const { creditorSupplier } = original;
  const { orderBy, billingBy, scheduleBy } = creditorSupplier!.common; // tslint:disable-line:no-non-null-assertion

  const { email } = current.common;

  return {
    ...creditorSupplier!.common, // tslint:disable-line:no-non-null-assertion
    billingBy: getCreditorDestinationOriginalValue(billingBy, email),
    orderBy: getCreditorDestinationOriginalValue(orderBy, email),
    scheduleBy: getCreditorDestinationOriginalValue(scheduleBy, email),
  };
}

/**
 * Merges destination info with provided email, if any.
 *
 * @param destination
 * @param email
 */
export function getCreditorDestinationOriginalValue(destination: ContactDetailsCreditorDeliveryInfo,
                                                    email?: string): ContactDetailsCreditorDeliveryInfo {
  return {
    ...destination,
    deliveryMethod: email ? ContactDeliveryMethod.Email : ContactDeliveryMethod.Mail,
    emailMatch: email ? EmailMatch.Same : EmailMatch.Different,
    email: email || destination.email,
  };
}

export function contactDetailsCreditorCommonFromDto(dto: ContactDetailsCreditorCommonDto,
                                                    root: ContactDetails): ContactDetailsCreditorCommon {
  const { orderBy, billingBy, scheduleBy } = dto;

  const { email } = root.common;

  return {
    ...dto,
    billingBy: getNormalizedCreditorDestination(billingBy, email),
    orderBy: getNormalizedCreditorDestination(orderBy, email),
    scheduleBy: getNormalizedCreditorDestination(scheduleBy, email),
  };
}

export function getNormalizedCreditorDestination(dto: ContactDetailsCreditorDeliveryInfoDto,
                                                 email?: string): ContactDetailsCreditorDeliveryInfo {
  const useSameEmail = dto.emailMatch === EmailMatch.Same;

  return { ...dto, email: useSameEmail ? email : dto.email };
}
